import React, { useCallback, useState, type ReactElement } from 'react';

import { cssMap } from '@compiled/react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { markdownModeMessages as i18n } from '@atlaskit/editor-common/messages';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import PencilIcon from '@atlaskit/icon-lab/core/pencil';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message/message';
import { token } from '@atlaskit/tokens';

import type { MarkdownConvertDirection } from '../markdownModePluginType';

const TEST_ID = 'convert-markdown-mode-dialog';

const styles = cssMap({
	iconBox: {
		height: '24px',
		width: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		alignSelf: 'start',
	},
	rowText: {
		flex: 1,
	},
	titleText: {
		display: 'inline',
	},
	lozengeWrap: {
		flexShrink: 0,
	},
	bodyStack: {
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.100'),
	},
	footer: {
		width: '100%',
	},
});

type IconRow = {
	icon: ReactElement;
	label: MessageDescriptor;
	testId: string;
};

type Variant = 'toMarkdown' | 'toMarkdownLossy' | 'toRichText';

const resolveVariant = (direction: MarkdownConvertDirection, isLossy: boolean): Variant => {
	if (direction === 'toRichText') {
		return 'toRichText';
	}
	return isLossy ? 'toMarkdownLossy' : 'toMarkdown';
};

const buildRows = (variant: Variant): IconRow[] => {
	switch (variant) {
		case 'toMarkdownLossy':
			return [
				{
					icon: <PencilIcon label="" />,
					label: i18n.confirmDialogToMarkdownLossyRichContent,
					testId: `${TEST_ID}--row-lossy-rich-content`,
				},
				{
					icon: <HistoryIcon label="" />,
					label: i18n.confirmDialogToMarkdownLossyVersionHistory,
					testId: `${TEST_ID}--row-lossy-version-history`,
				},
			];
		case 'toMarkdown':
			return [
				{
					icon: <PencilIcon label="" />,
					label: i18n.confirmDialogToMarkdownToolsRemoved,
					testId: `${TEST_ID}--row-tools-removed`,
				},
				{
					icon: <AngleBracketsIcon label="" />,
					label: i18n.confirmDialogToMarkdownToggle,
					testId: `${TEST_ID}--row-toggle`,
				},
				{
					icon: <HistoryIcon label="" />,
					label: i18n.confirmDialogToMarkdownVersionHistory,
					testId: `${TEST_ID}--row-version-history`,
				},
			];
		case 'toRichText':
		default:
			return [
				{
					icon: <PencilIcon label="" />,
					label: i18n.confirmDialogToRichTextStandardTools,
					testId: `${TEST_ID}--row-standard-tools`,
				},
				{
					icon: <HistoryIcon label="" />,
					label: i18n.confirmDialogToRichTextVersionHistory,
					testId: `${TEST_ID}--row-version-history`,
				},
			];
	}
};

const InlineRow = ({ icon, label, testId }: IconRow) => {
	const intl = useIntl();
	return (
		<Inline space="space.100" alignBlock="start">
			<Box xcss={styles.iconBox} testId={testId}>
				{icon}
			</Box>
			<Box as="span" xcss={styles.rowText}>
				{intl.formatMessage(label)}
			</Box>
		</Inline>
	);
};

const resolveTitle = (variant: Variant): MessageDescriptor => {
	switch (variant) {
		case 'toMarkdownLossy':
			return i18n.confirmDialogTitleToMarkdownLossy;
		case 'toMarkdown':
			return i18n.confirmDialogTitleToMarkdown;
		case 'toRichText':
		default:
			return i18n.confirmDialogTitleToRichText;
	}
};

const resolveConfirmLabel = (variant: Variant): MessageDescriptor =>
	variant === 'toMarkdownLossy' ? i18n.confirmDialogToMarkdownLossyCta : i18n.confirmDialogConfirm;

export type ConvertMarkdownModeDialogBaseProps = {
	/** Conversion direction being confirmed. Drives the title + body copy. */
	direction: MarkdownConvertDirection;
	/**
	 * Whether the conversion will lose information. Only meaningful for
	 * `direction === 'toMarkdown'`. When `true`, the dialog switches to a
	 * warning treatment (yellow title, yellow CTA, lossy copy + 2 rows).
	 * Ignored for `toRichText`.
	 */
	isLossy: boolean;
	/** Whether the modal is currently visible. */
	isOpen: boolean;
	/** Called when the user dismisses (cancel button, escape, blanket click). */
	onClose: () => void;
	/**
	 * Called when the user confirms. Should run the conversion and resolve
	 * to `{ success }` so the dialog can render the inline error state on
	 * failure or close itself on success.
	 */
	onConfirm: () => Promise<{ success: boolean }>;
	/** Number of synced blocks in the doc. `>0` adds warning treatment + copy. */
	syncBlockCount?: number;
};

const ConvertMarkdownModeDialogContents = ({
	direction,
	isLossy,
	syncBlockCount = 0,
	onConfirm,
	onClose,
}: Omit<ConvertMarkdownModeDialogBaseProps, 'isOpen'>) => {
	const intl = useIntl();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasError, setHasError] = useState(false);

	const handleConfirm = useCallback(async () => {
		setHasError(false);
		setIsSubmitting(true);
		try {
			const { success } = await onConfirm();
			if (success) {
				onClose();
			} else {
				setHasError(true);
			}
		} catch {
			setHasError(true);
		} finally {
			setIsSubmitting(false);
		}
	}, [onConfirm, onClose]);

	const variant = resolveVariant(direction, isLossy);
	const isWarning = variant === 'toMarkdownLossy' || syncBlockCount > 0;
	const rows = buildRows(variant);
	const titleMessage = resolveTitle(variant);
	const confirmLabel =
		variant === 'toMarkdown' && syncBlockCount > 0
			? i18n.confirmDialogToMarkdownLossyCta
			: resolveConfirmLabel(variant);

	return (
		<>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance={isWarning ? 'warning' : undefined}>
					<Inline as="span" space="space.100" alignBlock="start">
						<Box as="span" xcss={styles.titleText}>
							{intl.formatMessage(titleMessage)}
						</Box>
						<Box as="span" xcss={styles.lozengeWrap}>
							<Lozenge appearance="discovery">{intl.formatMessage(i18n.labsLozenge)}</Lozenge>
						</Box>
					</Inline>
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.150" xcss={styles.bodyStack}>
					{rows.map((row) => (
						<InlineRow key={row.testId} icon={row.icon} label={row.label} testId={row.testId} />
					))}
					{syncBlockCount > 0 ? (
						<SectionMessage
							appearance="warning"
							title={intl.formatMessage(i18n.unlinkSyncedBlocksTitle)}
							testId={`${TEST_ID}--synced-content-warning`}
						>
							{intl.formatMessage(i18n.unlinkSyncedBlocksBody)}
						</SectionMessage>
					) : null}
					{hasError ? (
						<SectionMessage appearance="error" testId={`${TEST_ID}--error`}>
							{intl.formatMessage(i18n.confirmDialogError)}
						</SectionMessage>
					) : null}
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Box xcss={styles.footer}>
					<Inline alignBlock="center" alignInline="end" space="space.100">
						<Button
							appearance="subtle"
							onClick={onClose}
							isDisabled={isSubmitting}
							testId={`${TEST_ID}--cancel`}
						>
							{intl.formatMessage(i18n.confirmDialogCancel)}
						</Button>
						<Button
							autoFocus={true}
							appearance={isWarning ? 'warning' : 'primary'}
							onClick={handleConfirm}
							isDisabled={isSubmitting}
							testId={`${TEST_ID}--confirm`}
						>
							{intl.formatMessage(confirmLabel)}
						</Button>
					</Inline>
				</Box>
			</ModalFooter>
		</>
	);
};

/**
 * Presentational confirmation dialog for markdown-mode conversion. Renders
 * one of three variants:
 *
 * - `direction === 'toRichText'`: confirmation, primary CTA "Confirm".
 * - `direction === 'toMarkdown' && !isLossy`: confirmation, primary CTA "Confirm".
 * - `direction === 'toMarkdown' && isLossy`: warning treatment (yellow title,
 *   yellow CTA "Convert to Markdown", lossy-specific 2-row body).
 *
 * Stateless w.r.t. open/close: the consumer owns the dialog state (sweet-state
 * store, plugin state, local React state, …) and feeds it in. This keeps the
 * component reusable across products that don't share the same state model.
 */
export const ConvertMarkdownModeDialogBase = ({
	isOpen,
	direction,
	isLossy,
	syncBlockCount = 0,
	onConfirm,
	onClose,
}: ConvertMarkdownModeDialogBaseProps): ReactElement => (
	<ModalTransition>
		{isOpen ? (
			<Modal onClose={onClose} width="small" testId={TEST_ID}>
				<ConvertMarkdownModeDialogContents
					direction={direction}
					isLossy={isLossy}
					syncBlockCount={syncBlockCount}
					onConfirm={onConfirm}
					onClose={onClose}
				/>
			</Modal>
		) : null}
	</ModalTransition>
);
